import React from 'react';
import { makeStyles, Checkbox, FormControlLabel } from '@material-ui/core';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { designTokens } from '../../../styles/designTokens';
import { FILTER_CONFIG } from '../utils/filterConfig';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        paddingBottom: designTokens.spacing.sm,
        marginBottom: designTokens.spacing.sm,
        borderBottom: `1px solid ${designTokens.colors.blue.light}`,
    },
    subtitle: {
        ...designTokens.typography.subtitle,
        margin: 0,
    },
    clearButton: {
        ...designTokens.typography.subtitle,
        color: designTokens.colors.red.main,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        gap: designTokens.spacing.sm,
        transition: 'color 0.2s ease',
        '&:hover': {
            color: designTokens.colors.red.main,
            '& $closeIcon path': {
                fill: designTokens.colors.red.main,
                fillOpacity: 1,
            }
        }
    },
    closeIcon: {
        width: '14px',
        height: '14px',
        '& path': {
            transition: 'fill 0.2s ease',
        }
    },
    filterList: {
        display: 'flex',
        flexDirection: 'column',
        gap: designTokens.spacing.sm,
    },
    filterItem: {
        margin: 0,
        padding: designTokens.spacing.sm,
        borderRadius: designTokens.borderRadius.card,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        '&:hover': {
            backgroundColor: designTokens.colors.grey[100],
        }
    },
    filterItemLabel: {
        flex: 1,
        marginRight: designTokens.spacing.sm,
        display: 'flex',
        width: '100%',
    },
    filterLabel: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
    filterInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: designTokens.spacing.sm,
    },
    filterRight: {
        display: 'flex',
        alignItems: 'center',
        gap: designTokens.spacing.md,
        marginLeft: 'auto',
    },
    filterIcon: {
        width: '32px',
        height: '32px',
        objectFit: 'contain',
    },
    filterText: {
        ...designTokens.typography.label,
        color: designTokens.colors.blue.main,
        fontSize: '15px',
    },
    filterCount: {
        ...designTokens.typography.body,
        lineHeight: '18px',
        color: designTokens.colors.grey[400],
    },
    checkbox: {
        color: designTokens.colors.primary,
        padding: 0,
        width: '20px',
        height: '20px',
        '&.Mui-checked': {
            color: designTokens.colors.primary,
        },
        '& .MuiIconButton-label': {
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
        }
    },
    checkIcon: {
        width: '20px',
        height: '20px',
        display: 'block',
    }
});

/**
 * Компонент панелі фільтрів
 * @param {Object} props
 * @param {Object} props.selectedFilters - Об'єкт вибраних фільтрів
 * @param {Function} props.onFilterChange - Callback зміни фільтру
 * @param {Function} props.onClearAll - Callback очищення всіх фільтрів
 * @param {Object} props.filterCounts - Об'єкт з кількістю пунктів для кожного фільтру
 */
const CollectionPointsFilter = ({
    selectedFilters,
    onFilterChange,
    onClearAll,
    filterCounts
}) => {
    const classes = useStyles();
    const hasActiveFilters = Object.values(selectedFilters).some(v => v);

    const handleCheckboxChange = (filterId) => (event) => {
        onFilterChange(filterId, event.target.checked);
    };

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                {hasActiveFilters ? (
                    <button className={classes.clearButton} onClick={onClearAll}>
                        Очистити всі
                        <svg className={classes.closeIcon} viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.13219 1.04434C8.99985 0.911701 8.82018 0.837161 8.63281 0.837161C8.44545 0.837161 8.26578 0.911701 8.13344 1.04434L4.66969 4.50101L1.20594 1.03726C1.0736 0.904618 0.893929 0.830078 0.706562 0.830078C0.519196 0.830078 0.339526 0.904618 0.207187 1.03726C-0.0690625 1.31351 -0.0690625 1.75976 0.207187 2.03601L3.67094 5.49976L0.207187 8.96351C-0.0690625 9.23976 -0.0690625 9.68601 0.207187 9.96226C0.483437 10.2385 0.929687 10.2385 1.20594 9.96226L4.66969 6.49851L8.13344 9.96226C8.40969 10.2385 8.85594 10.2385 9.13219 9.96226C9.40844 9.68601 9.40844 9.23976 9.13219 8.96351L5.66844 5.49976L9.13219 2.03601C9.40135 1.76684 9.40135 1.31351 9.13219 1.04434Z"
                                  fill="#DC7663"/>
                        </svg>
                    </button>
                ) : (
                    <p className={classes.subtitle}>Оберіть фільтр</p>
                )}
            </div>

            <div className={classes.filterList}>
                {FILTER_CONFIG.map(filter => (
                    <FormControlLabel
                        key={filter.id}
                        className={classes.filterItem}
                        classes={{
                            label: classes.filterItemLabel
                        }}
                        labelPlacement="start"
                        control={
                            <Checkbox
                                icon={<RadioButtonUncheckedIcon />}
                                checkedIcon={
                                    <img
                                        src="/icons/collection-points/check.svg"
                                        alt="checked"
                                        className={classes.checkIcon}
                                    />
                                }
                                checked={!!selectedFilters[filter.id]}
                                onChange={handleCheckboxChange(filter.id)}
                                className={classes.checkbox}
                            />
                        }
                        label={
                            <div className={classes.filterLabel}>
                                <div className={classes.filterInfo}>
                                    <img
                                        src={filter.icon}
                                        alt={filter.label}
                                        className={classes.filterIcon}
                                    />
                                    <span className={classes.filterText}>{filter.label}</span>
                                </div>
                                <div className={classes.filterRight}>
                                    <span className={classes.filterCount}>
                                        {filterCounts[filter.id] || 0}
                                    </span>
                                </div>
                            </div>
                        }
                    />
                ))}
            </div>
        </div>
    );
};

export default CollectionPointsFilter;
